import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/lib/notify";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Save, Lock, Unlock, Plus, Trash2, UserCog, CheckCircle2, XCircle, Shield,
  BarChart3, Eye, FileText, Download, Users, Clock, AlertCircle, Settings2,
} from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmailConfirmDialog } from "@/components/EmailConfirmDialog";
import InvestorTypeSelector from "@/components/InvestorTypeSelector";

const STATUS_STYLES = { draft: "bg-amber-500/10 text-amber-400 border-amber-500/20", active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", closed: "bg-red-500/10 text-red-400 border-red-500/20" };
const FIELD_TYPES = [{ value: "text", label: "Text" }, { value: "number", label: "Number" }, { value: "email", label: "Email" }, { value: "phone", label: "Phone" }, { value: "date", label: "Date" }, { value: "textarea", label: "Text Area" }, { value: "select", label: "Dropdown" }];
const FUND_TYPES = ["Hedge Fund", "Digital Asset Fund", "Private Equity", "Venture Capital", "Real Estate Fund", "Fund of Funds", "Other"];
const OPEN_ENDED_TYPES = ["hedge fund", "digital asset fund", "digital asset", "crypto"];
const CLOSED_ENDED_TYPES = ["private equity", "venture capital", "real estate fund"];
const OFFERING_TYPES = [{ value: "shares", label: "Shares" }, { value: "units", label: "Units" }, { value: "interest", label: "Interest" }];
const STATEMENT_TEMPLATES = [
  { value: "template_1", label: "Template 1 - NAV Statement", desc: "Account Summary (MTD/YTD), Shares Summary, Closing NAV", category: "open_ended" },
  { value: "template_2", label: "Template 2 - Equity Statement", desc: "Capital Additions/Withdrawals, Fee Allocations, Net Return", category: "open_ended" },
  { value: "template_3", label: "Template 3 - Partner Capital", desc: "Capital Commitment, MOIC, Operating Activity (QTD/YTD/Inception)", category: "closed_ended" },
];

function detectCategory(fundType) {
  const t = (fundType || "").toLowerCase();
  for (const ct of CLOSED_ENDED_TYPES) { if (t.includes(ct)) return "closed_ended"; }
  for (const ot of OPEN_ENDED_TYPES) { if (t.includes(ot)) return "open_ended"; }
  return "open_ended";
}

const emptyOffering = () => ({
  id: crypto.randomUUID(), name: "", type: "shares",
  management_fee: "", mf_logic: "", performance_fee: "", pf_logic: "",
  hurdle_rate: "", hurdle_logic: "", high_watermark: "", loss_carry_forward: "", clawback: "", others: "",
});

export default function FundDetail() {
  const { fundId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const backPath = isAdmin ? "/admin/fund" : "/super-admin/funds";
  const [fund, setFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState({});
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [fields, setFields] = useState([]);
  const [docs, setDocs] = useState([]);
  const [reportingFreq, setReportingFreq] = useState("quarterly");
  const [reportingStartDate, setReportingStartDate] = useState("");
  const [fundCategory, setFundCategory] = useState("open_ended");
  const [offerings, setOfferings] = useState([]);
  const [statementTemplate, setStatementTemplate] = useState("template_1");
  const [logoUploading, setLogoUploading] = useState(false);
  const [sharesApplicable, setSharesApplicable] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [allAdmins, setAllAdmins] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  // Reporting tab state
  const [reportingPeriods, setReportingPeriods] = useState([]);
  const [reportingLoading, setReportingLoading] = useState(false);
  const [selectedReportingPeriod, setSelectedReportingPeriod] = useState(null);
  const [rollOnData, setRollOnData] = useState({ rows: [], periods: [] });
  const [rollOnLoading, setRollOnLoading] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(null);
  const [confirmReleaseAll, setConfirmReleaseAll] = useState(false);
  // Onboarding summary state
  const [onboardingSummary, setOnboardingSummary] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  // Fund config state
  const [fundConfig, setFundConfig] = useState({ fm_can_edit_investor: true, fm_can_request_docs: true, fm_requires_approval: false, fm_approval_enabled: false });
  const [fundConfigLoading, setFundConfigLoading] = useState(false);
  // Email confirm state
  const [emailConfirm, setEmailConfirm] = useState({ open: false, action: null, description: "" });

  const loadFund = useCallback(async () => {
    try {
      const { data } = await API.get(`/funds/${fundId}`);
      setFund(data);
      setInfo({ name: data.name || "", fund_type: data.fund_type || "", strategy: data.strategy || "", terms: data.terms || "", investment_minimum: data.investment_minimum || "", currency: data.currency || "USD", management_fee: data.management_fee || "", incentive_fee: data.incentive_fee || "", nav: data.nav || "", fund_classification: data.fund_classification || "", decimal_precision: data.decimal_precision ?? 2 });
      setSelectedOrgId(data.org_id || "");
      setReportingFreq(data.reporting_frequency || "quarterly");
      setReportingStartDate(data.reporting_start_date || "");
      setFundCategory(data.fund_category || detectCategory(data.fund_type));
      setOfferings(data.offerings || []);
      // Auto-correct template if it doesn't match the fund category
      const cat = data.fund_category || detectCategory(data.fund_type);
      const tmpl = data.statement_template || "template_1";
      if (cat === "closed_ended" && tmpl !== "template_3") {
        setStatementTemplate("template_3");
      } else if (cat === "open_ended" && tmpl === "template_3") {
        setStatementTemplate("template_1");
      } else {
        setStatementTemplate(tmpl);
      }
      setSharesApplicable(data.shares_applicable || false);
      setFields(data.onboarding_workflow?.required_fields || []);
      setDocs(data.onboarding_workflow?.required_documents || []);
      // Load fund config
      if (data.fund_config) {
        setFundConfig(prev => ({ ...prev, ...data.fund_config }));
      }
    } catch { toast.error("Failed to load fund"); navigate(backPath); }
    finally { setLoading(false); }
  }, [fundId, navigate, backPath]);

  useEffect(() => { loadFund(); }, [loadFund]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/organizations");
        setOrgs(data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const workflow = fund?.onboarding_workflow || {};
  const isLocked = workflow.locked;

  const saveInfo = async () => {
    if (!reportingFreq) { toast.error("Reporting frequency is required"); return; }
    setSaving(true);
    try {
      const classes = offerings.map(o => o.name).filter(Boolean);
      await API.put(`/funds/${fundId}`, {
        ...info,
        org_id: selectedOrgId || undefined,
        investment_minimum: parseFloat(info.investment_minimum) || 0,
        nav: parseFloat(info.nav) || 0,
        reporting_frequency: reportingFreq,
        reporting_start_date: reportingStartDate || null,
        fund_category: fundCategory,
        fund_classification: info.fund_classification || null,
        decimal_precision: info.decimal_precision ?? 2,
        class_type: offerings.length > 1 ? "multi" : "single",
        classes,
        offerings,
        shares_applicable: sharesApplicable,
        statement_template: statementTemplate,
      });
      toast.success("Fund info updated");
      loadFund();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await API.post(`/funds/${fundId}/logo`, fd);
      toast.success("Logo uploaded"); loadFund();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to upload logo"); }
    finally { setLogoUploading(false); }
  };

  const handleFundTypeChange = (val) => {
    setInfo({ ...info, fund_type: val });
    const newCategory = detectCategory(val);
    setFundCategory(newCategory);
    // Auto-switch statement template when category changes
    if (newCategory === "closed_ended" && statementTemplate !== "template_3") {
      setStatementTemplate("template_3");
    } else if (newCategory === "open_ended" && statementTemplate === "template_3") {
      setStatementTemplate("template_1");
    }
  };

  const addOffering = () => setOfferings(prev => [...prev, emptyOffering()]);
  const removeOffering = (idx) => setOfferings(prev => prev.filter((_, i) => i !== idx));
  const updateOffering = (idx, key, val) => setOfferings(prev => prev.map((o, i) => i === idx ? { ...o, [key]: val } : o));

  const saveWorkflow = async () => {
    setSaving(true);
    try {
      await API.put(`/funds/${fundId}/workflow`, { required_fields: fields, required_documents: docs });
      toast.success("Workflow updated"); loadFund();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const toggleLock = async () => {
    try {
      await API.put(`/funds/${fundId}/workflow/${isLocked ? "unlock" : "lock"}`);
      toast.success(isLocked ? "Workflow unlocked" : "Workflow locked"); loadFund();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const changeStatus = async (status) => {
    try {
      await API.put(`/funds/${fundId}`, { status });
      toast.success(`Fund ${status === "active" ? "activated" : "closed"}`); loadFund();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openAssignDialog = async () => {
    try {
      const { data } = await API.get("/users?role=admin&status=active");
      setAllAdmins(data);
      setSelectedAdmins(fund?.admin_ids || []);
      setAssignDialog(true);
    } catch { toast.error("Failed to load admins"); }
  };

  const saveAdmins = async () => {
    try {
      await API.post(`/funds/${fundId}/admins`, { admin_ids: selectedAdmins });
      toast.success("Admins updated"); setAssignDialog(false); loadFund();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const updateField = (i, k, v) => setFields(p => p.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const addField = () => setFields(p => [...p, { id: crypto.randomUUID(), field_name: "", field_type: "text", required: true }]);
  const removeField = (i) => setFields(p => p.filter((_, idx) => idx !== i));
  const updateDoc = (i, k, v) => setDocs(p => p.map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  const addDoc = () => setDocs(p => [...p, { id: crypto.randomUUID(), doc_name: "", description: "", required: true }]);
  const removeDoc = (i) => setDocs(p => p.filter((_, idx) => idx !== i));

  // Reporting tab functions
  const loadReportingPeriods = useCallback(async () => {
    if (!fundId) return;
    setReportingLoading(true);
    try {
      const { data } = await API.get(`/funds/${fundId}/reporting-period-summary`);
      setReportingPeriods(data.periods || []);
    } catch { toast.error("Failed to load reporting periods"); }
    finally { setReportingLoading(false); }
  }, [fundId]);

  const loadRollOnForPeriod = async (period) => {
    setSelectedReportingPeriod(period);
    setRollOnLoading(true);
    try {
      const { data } = await API.get(`/funds/${fundId}/roll-on-report?period_id=${period.id}`);
      setRollOnData(data);
    } catch { toast.error("Failed to load report"); }
    finally { setRollOnLoading(false); }
  };

  const releaseAllForPeriod = async (periodId) => {
    try {
      await API.patch(`/funds/${fundId}/performance-transactions/release-all?period_id=${periodId}`);
      toast.success("All entries released for this period");
      loadReportingPeriods();
      if (selectedReportingPeriod?.id === periodId) loadRollOnForPeriod(selectedReportingPeriod);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to release"); }
  };

  const releaseSingle = async (perfTxnId) => {
    try {
      await API.patch(`/funds/${fundId}/performance-transactions/${perfTxnId}/release`);
      toast.success("Entry released");
      if (selectedReportingPeriod) loadRollOnForPeriod(selectedReportingPeriod);
      loadReportingPeriods();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to release"); }
  };

  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const loadOnboardingSummary = async () => {
    setOnboardingLoading(true);
    try {
      const { data } = await API.get(`/funds/${fundId}/onboarding-summary`);
      setOnboardingSummary(data);
    } catch { toast.error("Failed to load onboarding summary"); }
    finally { setOnboardingLoading(false); }
  };

  const loadFundConfig = async () => {
    setFundConfigLoading(true);
    try {
      const { data } = await API.get(`/funds/${fundId}/config`);
      setFundConfig(data);
    } catch { /* defaults are fine */ }
    finally { setFundConfigLoading(false); }
  };

  const saveFundConfig = async () => {
    setSaving(true);
    try {
      const { data } = await API.put(`/funds/${fundId}/config`, fundConfig);
      setFundConfig(data);
      toast.success("Fund configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save config"); }
    finally { setSaving(false); }
  };

  const handleReleaseWithEmailConfirm = (periodId) => {
    setEmailConfirm({
      open: true,
      description: "Releasing all entries will send email notifications to investors with outgoing emails enabled. Continue?",
      action: () => releaseAllForPeriod(periodId),
    });
  };

  const handleReleaseSingleWithConfirm = (perfTxnId) => {
    setEmailConfirm({
      open: true,
      description: "Releasing this entry may send an email notification to the investor. Continue?",
      action: () => releaseSingle(perfTxnId),
    });
  };


  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fund-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(backPath)} data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-3xl font-bold tracking-tight">{fund?.name}</h1>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_STYLES[fund?.status]}`}>{fund?.status}</Badge>
              <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{fundCategory === "open_ended" ? "Open Ended" : "Closed Ended"}</Badge>
            </div>
            <p className="text-muted-foreground text-sm mt-1">{fund?.fund_type} {fund?.reporting_frequency ? `| ${fund.reporting_frequency} reporting` : ""}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {fund?.status === "draft" && <Button variant="secondary" className="border border-border" onClick={() => changeStatus("active")} data-testid="activate-fund-button"><CheckCircle2 className="w-4 h-4 mr-2" /> Activate</Button>}
          {fund?.status === "active" && !isAdmin && <Button variant="secondary" className="border border-border text-red-400" onClick={() => setConfirmClose(true)} data-testid="close-fund-button"><XCircle className="w-4 h-4 mr-2" /> Close Fund</Button>}
          {fund?.status === "closed" && !isAdmin && <Button variant="secondary" className="border border-border text-emerald-400" onClick={() => setConfirmReactivate(true)} data-testid="reactivate-fund-button"><CheckCircle2 className="w-4 h-4 mr-2" /> Re-activate</Button>}
        </div>
      </div>

      <Tabs defaultValue="info" className="space-y-6">
        <TabsList className="bg-secondary/50 border border-border" data-testid="fund-tabs">
          <TabsTrigger value="info">Fund Info</TabsTrigger>
          <TabsTrigger value="offerings">Offerings</TabsTrigger>
          <TabsTrigger value="workflow">Onboarding Workflow</TabsTrigger>
          {!isAdmin && <TabsTrigger value="admins">Fund Managers</TabsTrigger>}
          <TabsTrigger value="reporting" onClick={() => { if (reportingPeriods.length === 0) loadReportingPeriods(); }}>Reporting</TabsTrigger>
          <TabsTrigger value="onboarding" onClick={() => { if (!onboardingSummary) loadOnboardingSummary(); }}>KYC & Onboarding</TabsTrigger>
          {!isAdmin && <TabsTrigger value="fund-config" onClick={() => { loadFundConfig(); }}>Fund Config</TabsTrigger>}
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <Card>
            <CardContent className="p-6 space-y-4">
              {isAdmin && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-xs text-amber-400 font-medium">
                  Fund Info is read-only for Fund Managers. Contact Super Admin for changes.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Organization</Label>
                  <Select value={selectedOrgId} onValueChange={setSelectedOrgId} disabled={isAdmin}>
                    <SelectTrigger className="bg-secondary/50" data-testid="fund-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fund Name</Label>
                  <Input value={info.name || ""} onChange={e => setInfo({ ...info, name: e.target.value })} className="bg-secondary/50" data-testid="fund-name-input" disabled={isAdmin} />
                </div>
                <div className="space-y-2">
                  <Label>Fund Type</Label>
                  <Select value={info.fund_type || ""} onValueChange={handleFundTypeChange} disabled={isAdmin}>
                    <SelectTrigger className="bg-secondary/50" data-testid="fund-type-select"><SelectValue placeholder="Select fund type" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {FUND_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Auto-detected: <span className="font-medium">{fundCategory === "open_ended" ? "Open Ended" : "Closed Ended"}</span></p>
                </div>
                <div className="space-y-2">
                  <Label>Fund Classification</Label>
                  <Select value={info.fund_classification || ""} onValueChange={v => setInfo({ ...info, fund_classification: v })} disabled={isAdmin}>
                    <SelectTrigger className="bg-secondary/50" data-testid="fund-classification-select"><SelectValue placeholder="Select classification" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="new_launch">New Launch</SelectItem>
                      <SelectItem value="migration">Migration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Decimal Precision</Label>
                  <Select value={String(info.decimal_precision ?? 2)} onValueChange={v => setInfo({ ...info, decimal_precision: parseInt(v) })} disabled={isAdmin}>
                    <SelectTrigger className="bg-secondary/50" data-testid="decimal-precision-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="2">2 Decimals</SelectItem>
                      <SelectItem value="4">4 Decimals</SelectItem>
                      <SelectItem value="6">6 Decimals</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Decimal places for NAV/share calculations</p>
                </div>
                <div className="space-y-2">
                  <Label>Minimum Investment ($)</Label>
                  <Input type="number" value={info.investment_minimum || ""} onChange={e => setInfo({ ...info, investment_minimum: e.target.value })} className="bg-secondary/50" disabled={isAdmin} />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Input value={info.currency || ""} onChange={e => setInfo({ ...info, currency: e.target.value })} className="bg-secondary/50" disabled={isAdmin} />
                </div>
                <div className="space-y-2">
                  <Label>NAV</Label>
                  <Input type="number" value={info.nav || ""} onChange={e => setInfo({ ...info, nav: e.target.value })} className="bg-secondary/50" disabled={isAdmin} />
                </div>
                <div className="space-y-2">
                  <Label>Reporting Frequency *</Label>
                  <Select value={reportingFreq} onValueChange={setReportingFreq} disabled={isAdmin}>
                    <SelectTrigger className={`bg-secondary/50 ${!reportingFreq ? "border-amber-500/50" : ""}`}><SelectValue placeholder="Select frequency" /></SelectTrigger>
                    <SelectContent className="bg-card border-border"><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="annually">Annually</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Reporting Start Date</Label>
                  <Input type="date" value={reportingStartDate || ""} onChange={e => setReportingStartDate(e.target.value)} className="bg-secondary/50" data-testid="reporting-start-date-input" disabled={isAdmin} />
                  <p className="text-xs text-muted-foreground">The date from which reporting periods begin</p>
                </div>
                <div className="space-y-2">
                  <Label>Investor Statement Template</Label>
                  <Select value={statementTemplate} onValueChange={setStatementTemplate} disabled={isAdmin}>
                    <SelectTrigger className="bg-secondary/50" data-testid="statement-template-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {STATEMENT_TEMPLATES.filter(t => t.category === fundCategory).map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div><span className="font-medium">{t.label}</span></div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{STATEMENT_TEMPLATES.find(t => t.value === statementTemplate)?.desc}</p>
                  <p className="text-xs text-muted-foreground/60">
                    {fundCategory === "open_ended" ? "Open Ended funds use NAV or Equity statements" : "Closed Ended funds use Partner Capital statements"}
                  </p>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Fund Logo</Label>
                  <p className="text-xs text-muted-foreground mb-2">Used on investor statements and reports</p>
                  <div className="flex items-center gap-4">
                    {fund?.logo_file_name && (
                      <div className="w-16 h-16 rounded-lg border border-border bg-secondary/30 flex items-center justify-center overflow-hidden">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/funds/${fundId}/logo`} alt="Fund logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                    {!isAdmin && (
                      <>
                        <Button variant="secondary" size="sm" className="border border-border" onClick={() => document.getElementById("logo-upload").click()} disabled={logoUploading} data-testid="upload-logo-btn">
                          {logoUploading ? "Uploading..." : fund?.logo_file_name ? "Change Logo" : "Upload Logo"}
                        </Button>
                        <input id="logo-upload" type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={uploadLogo} />
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Share/Unit Tracking</Label>
                  <p className="text-xs text-muted-foreground mb-2">Enable if the fund uses share/unit-based accounting</p>
                  <Button variant={sharesApplicable ? "default" : "secondary"} size="sm" className="h-8 text-xs border border-border" onClick={() => !isAdmin && setSharesApplicable(!sharesApplicable)} disabled={isAdmin} data-testid="shares-applicable-toggle">
                    {sharesApplicable ? "Enabled" : "Disabled"}
                  </Button>
                </div>
              </div>
              <div className="space-y-2"><Label>Strategy</Label><Textarea value={info.strategy || ""} onChange={e => setInfo({ ...info, strategy: e.target.value })} className="bg-secondary/50 min-h-[80px]" disabled={isAdmin} /></div>
              <div className="space-y-2"><Label>Terms</Label><Textarea value={info.terms || ""} onChange={e => setInfo({ ...info, terms: e.target.value })} className="bg-secondary/50 min-h-[80px]" disabled={isAdmin} /></div>
              {!isAdmin && <div className="flex justify-end"><Button onClick={saveInfo} disabled={saving} data-testid="save-fund-info"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}</Button></div>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Offerings Tab */}
        <TabsContent value="offerings">
          <Card data-testid="offerings-card">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Fund Offerings</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">Define offering classes with fee structures and terms</p>
                </div>
                <Button variant="secondary" size="sm" className="border border-border" onClick={addOffering} data-testid="add-offering-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Offering
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {offerings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No offerings defined. Add at least one offering for this fund.
                </div>
              ) : (
                <div className="space-y-4">
                  {offerings.map((off, idx) => (
                    <Card key={off.id || idx} className="border border-border/50 bg-secondary/10" data-testid={`offering-${idx}`}>
                      <CardContent className="p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold">{off.name || `Offering ${idx + 1}`}</h4>
                          <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" onClick={() => removeOffering(idx)} data-testid={`remove-offering-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Offering Name</Label>
                            <Input value={off.name} onChange={e => updateOffering(idx, "name", e.target.value)} className="bg-secondary/50 h-8 text-sm" placeholder="e.g. Class A" data-testid={`offering-name-${idx}`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Type</Label>
                            <Select value={off.type} onValueChange={v => updateOffering(idx, "type", v)}>
                              <SelectTrigger className="bg-secondary/50 h-8 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-card border-border">{OFFERING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Management Fee (%)</Label>
                            <Input value={off.management_fee} onChange={e => updateOffering(idx, "management_fee", e.target.value)} className="bg-secondary/50 h-8 text-sm" placeholder="e.g. 2" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Performance Fee (%)</Label>
                            <Input value={off.performance_fee} onChange={e => updateOffering(idx, "performance_fee", e.target.value)} className="bg-secondary/50 h-8 text-sm" placeholder="e.g. 20" />
                          </div>
                        </div>
                        {/* Drivers */}
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Drivers</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1"><Label className="text-xs">MF Logic</Label><Textarea value={off.mf_logic} onChange={e => updateOffering(idx, "mf_logic", e.target.value)} className="bg-secondary/50 text-sm min-h-[60px]" placeholder="Management fee calculation logic..." /></div>
                            <div className="space-y-1"><Label className="text-xs">PF Logic</Label><Textarea value={off.pf_logic} onChange={e => updateOffering(idx, "pf_logic", e.target.value)} className="bg-secondary/50 text-sm min-h-[60px]" placeholder="Performance fee calculation logic..." /></div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            <div className="space-y-1"><Label className="text-xs">Hurdle Rate (%)</Label><Input value={off.hurdle_rate} onChange={e => updateOffering(idx, "hurdle_rate", e.target.value)} className="bg-secondary/50 h-8 text-sm" placeholder="e.g. 8" /></div>
                            <div className="space-y-1"><Label className="text-xs">Hurdle Logic</Label><Input value={off.hurdle_logic} onChange={e => updateOffering(idx, "hurdle_logic", e.target.value)} className="bg-secondary/50 h-8 text-sm" placeholder="Hurdle logic..." /></div>
                          </div>
                        </div>
                        {/* Terms & Conditions — Applicability Toggles */}
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Terms & Conditions</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                              { key: "high_watermark", label: "High Watermark", toggle: "high_watermark_applicable" },
                              { key: "loss_carry_forward", label: "Loss Carry Forward", toggle: "loss_carry_forward_applicable" },
                              { key: "clawback", label: "Clawback", toggle: "clawback_applicable" },
                              { key: "others", label: "Others", toggle: "others_applicable" },
                            ].map(term => (
                              <div key={term.key} className="space-y-2 p-3 rounded-lg bg-secondary/20 border border-border/30">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs font-semibold">{term.label}</Label>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-muted-foreground">{off[term.toggle] ? "Applicable" : "N/A"}</span>
                                    <Switch checked={!!off[term.toggle]} onCheckedChange={v => updateOffering(idx, term.toggle, v)} data-testid={`toggle-${term.key}-${idx}`} />
                                  </div>
                                </div>
                                {off[term.toggle] && (
                                  <Textarea value={off[term.key]} onChange={e => updateOffering(idx, term.key, e.target.value)} className="bg-secondary/50 text-sm min-h-[50px]" placeholder={`${term.label} details...`} />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              <div className="flex justify-end mt-4">
                <Button onClick={saveInfo} disabled={saving} data-testid="save-offerings-btn">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Offerings"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workflow Tab */}
        <TabsContent value="workflow" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className={`w-5 h-5 ${isLocked ? "text-emerald-400" : "text-amber-400"}`} />
              <span className="text-sm font-medium">{isLocked ? "Workflow is locked" : "Workflow is editable"}</span>
            </div>
            <Button variant="secondary" className="border border-border" onClick={toggleLock} data-testid="toggle-lock-button">
              {isLocked ? <><Unlock className="w-4 h-4 mr-2" /> Unlock</> : <><Lock className="w-4 h-4 mr-2" /> Lock Workflow</>}
            </Button>
          </div>
          <Card>
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Investor Information Fields</CardTitle>
                {!isLocked && <Button variant="secondary" size="sm" onClick={addField} className="border border-border" data-testid="add-field-button"><Plus className="w-4 h-4 mr-1" /> Add</Button>}
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-2">
              {fields.map((f, i) => (
                <div key={f.id || i} className="flex flex-col gap-2 p-3 rounded-lg bg-secondary/30 border border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input disabled={isLocked} placeholder="Field name" value={f.field_name} onChange={e => updateField(i, "field_name", e.target.value)} className="bg-secondary/50 text-sm" />
                      <Select disabled={isLocked} value={f.field_type} onValueChange={v => updateField(i, "field_type", v)}>
                        <SelectTrigger className="bg-secondary/50 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card border-border">{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      {f.field_type === "select" && <Input disabled={isLocked} placeholder="Options (comma-sep)" value={(f.options || []).join(", ")} onChange={e => updateField(i, "options", e.target.value.split(",").map(s => s.trim()))} className="bg-secondary/50 text-sm" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch disabled={isLocked} checked={f.required} onCheckedChange={v => updateField(i, "required", v)} />
                      {!isLocked && <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" onClick={() => removeField(i)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </div>
                  <div className="pl-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Applies To</p>
                    <InvestorTypeSelector
                      value={f.applicable_investor_types || f.applicable_entity_types || []}
                      onChange={(next) => updateField(i, "applicable_investor_types", next)}
                      disabled={isLocked}
                      testId={`field-types-${i}`}
                    />
                  </div>
                </div>
              ))}
              {fields.length === 0 && <p className="text-muted-foreground text-sm text-center py-4">No fields defined</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Required Documents</CardTitle>
                {!isLocked && <Button variant="secondary" size="sm" onClick={addDoc} className="border border-border" data-testid="add-doc-button"><Plus className="w-4 h-4 mr-1" /> Add</Button>}
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-2">
              {docs.map((d, i) => (
                <div key={d.id || i} className="flex flex-col gap-2 p-3 rounded-lg bg-secondary/30 border border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input disabled={isLocked} placeholder="Document name" value={d.doc_name} onChange={e => updateDoc(i, "doc_name", e.target.value)} className="bg-secondary/50 text-sm" />
                      <Input disabled={isLocked} placeholder="Description" value={d.description || ""} onChange={e => updateDoc(i, "description", e.target.value)} className="bg-secondary/50 text-sm" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch disabled={isLocked} checked={d.required} onCheckedChange={v => updateDoc(i, "required", v)} />
                      {!isLocked && <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" onClick={() => removeDoc(i)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </div>
                  <div className="pl-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Applies To</p>
                    <InvestorTypeSelector
                      value={d.applicable_investor_types || d.applicable_entity_types || []}
                      onChange={(next) => updateDoc(i, "applicable_investor_types", next)}
                      disabled={isLocked}
                      testId={`doc-types-${i}`}
                    />
                  </div>
                </div>
              ))}
              {docs.length === 0 && <p className="text-muted-foreground text-sm text-center py-4">No documents defined</p>}
            </CardContent>
          </Card>
          {!isLocked && (
            <div className="flex justify-end">
              <Button onClick={saveWorkflow} disabled={saving} data-testid="save-workflow-button"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Workflow"}</Button>
            </div>
          )}
        </TabsContent>

        {/* Admins Tab */}
        {!isAdmin && (
        <TabsContent value="admins">
          <Card>
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Assigned Fund Managers</CardTitle>
                <Button variant="secondary" size="sm" className="border border-border" onClick={openAssignDialog} data-testid="assign-admin-button"><Plus className="w-4 h-4 mr-1" /> Assign Fund Manager</Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {(!fund?.admin_ids || fund.admin_ids.length === 0) ? (
                <p className="text-muted-foreground text-sm text-center py-8">No admins assigned yet</p>
              ) : (
                <div className="space-y-2">
                  {fund.admin_details?.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">{a.name?.[0]}</div>
                        <div><p className="text-sm font-medium">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}</p></div>
                      </div>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-blue-500/10 text-blue-400 border-blue-500/20">Fund Manager</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Reporting Tab */}
        <TabsContent value="reporting">
          {!selectedReportingPeriod ? (
            <Card data-testid="reporting-periods-card">
              <CardHeader className="p-6 pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" /> Reporting Periods
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={loadReportingPeriods} className="text-xs">Refresh</Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                {reportingLoading ? (
                  <div className="space-y-2">
                    {[1,2,3].map(i => <div key={i} className="h-14 bg-secondary/30 rounded-lg animate-pulse" />)}
                  </div>
                ) : reportingPeriods.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground text-sm">
                    <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No reporting periods with data yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {reportingPeriods.map(p => (
                      <div key={p.id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 border border-border/50 group hover:border-primary/30 transition-colors cursor-pointer"
                        onClick={() => loadRollOnForPeriod(p)}
                        data-testid={`reporting-period-${p.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{p.label}</p>
                            <p className="text-xs text-muted-foreground">{p.investor_count} investor{p.investor_count !== 1 ? "s" : ""} | {p.total_entries} entr{p.total_entries !== 1 ? "ies" : "y"}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${p.status === "released" ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30"}`}>
                            {p.status === "released" ? "Released" : "Submitted"}
                          </Badge>
                          <Eye className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={() => { setSelectedReportingPeriod(null); loadReportingPeriods(); }} data-testid="back-to-periods">
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div>
                    <h2 className="text-lg font-semibold font-heading">{selectedReportingPeriod.label}</h2>
                    <p className="text-xs text-muted-foreground">
                      Roll-on report for all investors
                    </p>
                    <Badge variant="outline" className={`mt-1 text-[10px] uppercase tracking-wider ${selectedReportingPeriod.status === "released" ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30"}`}>
                      {selectedReportingPeriod.status === "released" ? "Released" : "Submitted"}
                    </Badge>
                  </div>
                </div>
                {selectedReportingPeriod.status !== "released" && !isAdmin && (
                  <Button size="sm" className="h-8 text-xs" onClick={() => handleReleaseWithEmailConfirm(selectedReportingPeriod.id)} data-testid="release-period-btn">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Release All
                  </Button>
                )}
              </div>
              <Card className="overflow-hidden" data-testid="period-roll-on-table">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/20 hover:bg-secondary/20 border-b border-border/50">
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold pl-4 min-w-[140px]">Investor</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold min-w-[100px]">Offering</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[110px]">Beginning Bal</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Additions</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Gross Income</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[90px]">Mgmt Fee</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[90px]">Incentive Fee</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[100px]">Redemptions</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[110px]">Ending Bal</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right min-w-[70px]">RoR</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold min-w-[80px] pr-4">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rollOnLoading ? [1,2,3].map(i => (
                          <TableRow key={i}><TableCell colSpan={11} className="pl-4"><div className="h-8 bg-secondary/30 rounded animate-pulse" /></TableCell></TableRow>
                        )) : rollOnData.rows?.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={11} className="text-center py-12 text-muted-foreground text-sm">No data for this period</TableCell>
                          </TableRow>
                        ) : rollOnData.rows?.map((r, idx) => (
                          <TableRow key={idx} data-testid={`period-row-${idx}`}>
                            <TableCell className="pl-4 text-sm font-medium">{r.investor_name}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{r.class_description || "-"}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{fmt(r.beginning_balance)}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{r.additions > 0 ? <span className="text-emerald-400">{fmt(r.additions)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{r.total_income > 0 ? <span className="text-emerald-400">{fmt(r.total_income)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{r.management_fee > 0 ? <span className="text-amber-400">{fmt(r.management_fee)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{r.incentive_fee > 0 ? <span className="text-violet-400">{fmt(r.incentive_fee)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{r.redemptions > 0 ? <span className="text-red-400">{fmt(r.redemptions)}</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums font-semibold">{fmt(r.ending_balance)}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{r.rate_of_return !== 0 ? <span className={r.rate_of_return >= 0 ? "text-emerald-400" : "text-red-400"}>{r.rate_of_return.toFixed(2)}%</span> : <span className="text-muted-foreground/40">-</span>}</TableCell>
                            <TableCell className="text-xs pr-4">
                              {r.status === "released" ? (
                                <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 text-[10px]">Released</Badge>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <Badge variant="outline" className="text-amber-400 border-amber-400/30 text-[10px]">Submitted</Badge>
                                  {!isAdmin && r.perf_txn_id && (
                                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-muted-foreground hover:text-primary px-1.5" onClick={() => handleReleaseSingleWithConfirm(r.perf_txn_id)} data-testid={`release-single-${idx}`}>
                                      Release
                                    </Button>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground">{rollOnData.rows?.length || 0} row{rollOnData.rows?.length !== 1 ? "s" : ""}</p>
            </div>
          )}
        </TabsContent>

        {/* KYC & Onboarding Tab */}
        <TabsContent value="onboarding">
          <Card data-testid="onboarding-summary-card">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" /> KYC & Onboarding Overview
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={loadOnboardingSummary} className="text-xs">Refresh</Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {onboardingLoading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-secondary/30 rounded animate-pulse" />)}</div>
              ) : !onboardingSummary ? (
                <p className="text-muted-foreground text-sm text-center py-8">Click refresh to load data</p>
              ) : (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: "Pending", count: onboardingSummary.onboarding_counts.pending, color: "text-amber-400", bg: "bg-amber-500/10" },
                      { label: "In Progress", count: onboardingSummary.onboarding_counts.in_progress, color: "text-blue-400", bg: "bg-blue-500/10" },
                      { label: "Submitted", count: onboardingSummary.onboarding_counts.submitted, color: "text-violet-400", bg: "bg-violet-500/10" },
                      { label: "Complete", count: onboardingSummary.onboarding_counts.complete, color: "text-emerald-400", bg: "bg-emerald-500/10" },
                    ].map(s => (
                      <div key={s.label} className={`rounded-lg border border-border/50 p-4 ${s.bg}`}>
                        <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
                        <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {/* KYC Summary */}
                  <div>
                    <p className="text-sm font-semibold mb-2">KYC Status</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(onboardingSummary.kyc_counts).map(([status, count]) => (
                        <Badge key={status} variant="outline" className="text-xs px-3 py-1">
                          {status}: <span className="font-bold ml-1">{count}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {/* Investor List */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/20 hover:bg-secondary/20">
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold pl-4">Investor</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Email</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Onboarding</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold pr-4">KYC</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {onboardingSummary.investors.map((inv, idx) => (
                          <TableRow key={inv.id} data-testid={`onboarding-row-${idx}`}>
                            <TableCell className="pl-4 text-sm font-medium">{inv.name}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{inv.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] ${inv.onboarding_status === "complete" ? "text-emerald-400 border-emerald-400/30" : inv.onboarding_status === "submitted" ? "text-violet-400 border-violet-400/30" : "text-amber-400 border-amber-400/30"}`}>
                                {inv.onboarding_status?.replace("_", " ")}
                              </Badge>
                            </TableCell>
                            <TableCell className="pr-4">
                              <Badge variant="outline" className={`text-[10px] ${inv.kyc_status === "Verified" ? "text-emerald-400 border-emerald-400/30" : inv.kyc_status === "Flagged" ? "text-red-400 border-red-400/30" : "text-amber-400 border-amber-400/30"}`}>
                                {inv.kyc_status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                        {onboardingSummary.investors.length === 0 && (
                          <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground text-sm">No investors in this fund</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fund Config Tab (Super Admin Only) */}
        {!isAdmin && (
        <TabsContent value="fund-config">
          <Card data-testid="fund-config-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-primary" /> Fund Manager Permissions
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Control what Fund Managers can do within this fund</p>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-5">
              {fundConfigLoading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-secondary/30 rounded animate-pulse" />)}</div>
              ) : (
                <>
                  <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-secondary/30 border border-border/50" data-testid="config-fm-edit-investor">
                    <div>
                      <p className="text-sm font-medium">FM Can Edit Investor Details</p>
                      <p className="text-xs text-muted-foreground">Allow Fund Managers to modify investor information, distribution emails, and offerings</p>
                    </div>
                    <Switch checked={fundConfig.fm_can_edit_investor} onCheckedChange={v => setFundConfig(prev => ({ ...prev, fm_can_edit_investor: v }))} data-testid="toggle-fm-edit-investor" />
                  </div>
                  <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-secondary/30 border border-border/50" data-testid="config-fm-request-docs">
                    <div>
                      <p className="text-sm font-medium">FM Can Request Documents</p>
                      <p className="text-xs text-muted-foreground">Allow Fund Managers to send document upload requests to investors</p>
                    </div>
                    <Switch checked={fundConfig.fm_can_request_docs} onCheckedChange={v => setFundConfig(prev => ({ ...prev, fm_can_request_docs: v }))} data-testid="toggle-fm-request-docs" />
                  </div>
                  <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-secondary/30 border border-border/50" data-testid="config-fm-requires-approval">
                    <div>
                      <p className="text-sm font-medium">FM Requires Approval</p>
                      <p className="text-xs text-muted-foreground">Require Super Admin approval for Fund Manager actions on investors</p>
                    </div>
                    <Switch checked={fundConfig.fm_requires_approval} onCheckedChange={v => setFundConfig(prev => ({ ...prev, fm_requires_approval: v }))} data-testid="toggle-fm-requires-approval" />
                  </div>
                  <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-primary/5 border border-primary/20" data-testid="config-fm-approval-enabled">
                    <div>
                      <p className="text-sm font-medium">Enable Fund Manager Approval (Reporting)</p>
                      <p className="text-xs text-muted-foreground">When ON, statements must be approved by the Fund Manager before release. When OFF, Submit auto-approves and skips the Pending Approval / Rejected steps.</p>
                    </div>
                    <Switch checked={!!fundConfig.fm_approval_enabled} onCheckedChange={v => setFundConfig(prev => ({ ...prev, fm_approval_enabled: v }))} data-testid="toggle-fm-approval-enabled" />
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button onClick={saveFundConfig} disabled={saving} data-testid="save-fund-config-btn">
                      <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Configuration"}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="assign-admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><UserCog className="w-5 h-5 text-primary" /> Assign Fund Managers</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">Select admins to manage this fund</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {allAdmins.length === 0 ? <p className="text-muted-foreground text-sm text-center py-4">No fund managers available</p> : allAdmins.map(a => (
              <label key={a.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/30 cursor-pointer transition-colors">
                <Checkbox checked={selectedAdmins.includes(a.id)} onCheckedChange={checked => setSelectedAdmins(prev => checked ? [...prev, a.id] : prev.filter(id => id !== a.id))} />
                <div><p className="text-sm font-medium">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}</p></div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignDialog(false)}>Cancel</Button>
            <Button onClick={saveAdmins} data-testid="save-admins-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        title="Close Fund"
        description={`Are you sure you want to close "${fund?.name}"? This will prevent any further transactions.`}
        confirmLabel="Close Fund"
        onConfirm={() => changeStatus("closed")}
      />
      <ConfirmDialog
        open={confirmReleaseAll}
        onOpenChange={setConfirmReleaseAll}
        title="Release All Entries"
        description={`Are you sure you want to release all entries for ${selectedReportingPeriod?.label}? This will mark all performance transactions as released for this period.`}
        confirmLabel="Release All"
        variant="default"
        onConfirm={() => releaseAllForPeriod(selectedReportingPeriod?.id)}
      />
      <ConfirmDialog
        open={confirmReactivate}
        onOpenChange={setConfirmReactivate}
        title="Re-activate Fund"
        description={`Are you sure you want to re-activate "${fund?.name}"? This will allow transactions and reporting to resume.`}
        confirmLabel="Re-activate"
        variant="default"
        onConfirm={() => changeStatus("active")}
      />
      <EmailConfirmDialog
        open={emailConfirm.open}
        onOpenChange={(v) => setEmailConfirm(prev => ({ ...prev, open: v }))}
        description={emailConfirm.description}
        onConfirm={() => { if (emailConfirm.action) emailConfirm.action(); }}
      />
    </div>
  );
}
